package com.siriusxm.pia.views.unifiedaggregator.management

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.utils.toLocalDateTimeString
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import unifiedcontent.aggregator.AppConfigFeatureFlagsDocument
import unifiedcontent.aggregator.FeatureFlagHistoryEntry
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/**
 * Represents a changed feature flag between two versions.
 *
 * @property key The unique key identifier for the feature flag
 * @property flagName The human-readable name of the feature flag
 * @property wasEnabled The previous enabled state (null if this is a new flag)
 * @property isEnabled The current enabled state
 * @property attributes Additional attributes associated with the flag (excluding the enabled state)
 */
data class ChangedFlag(
    val key: String,
    val flagName: String,
    val wasEnabled: Boolean?,
    val isEnabled: Boolean,
    val attributes: JsonObject?
)

/**
 * Calculates which feature flags changed between two history entries.
 *
 * Compares the current history entry with the previous one to identify flags that were:
 * - Added (new flags)
 * - Removed (flags no longer present)
 * - Modified (enabled state or attributes changed)
 *
 * If no previous entry exists (first/oldest entry), returns all flags as changes.
 *
 * @param current The current history entry to analyze
 * @param previous The previous history entry to compare against (null for the oldest entry)
 * @param flags The feature flags document containing flag metadata (names, descriptions)
 * @return A list of ChangedFlag objects representing all detected changes
 */
fun calculateChangedFlags(
    current: FeatureFlagHistoryEntry,
    previous: FeatureFlagHistoryEntry?,
    flags: AppConfigFeatureFlagsDocument
): List<ChangedFlag> {
    val changes = mutableListOf<ChangedFlag>()
    val currentValues = current.document.values
    val previousValues = previous?.document?.values

    // If no previous entry, return all flags
    if (previousValues == null) {
        currentValues.forEach { (key, value) ->
            val flagDescriptor = flags.flags[key]
            if (flagDescriptor != null && value is JsonObject) {
                val isEnabled = value["enabled"]?.jsonPrimitive?.booleanOrNull ?: false
                changes.add(
                    ChangedFlag(
                        key = key,
                        flagName = flagDescriptor.name,
                        wasEnabled = null,
                        isEnabled = isEnabled,
                        attributes = value.filterKeys { it != "enabled" } as? JsonObject
                    )
                )
            }
        }
        return changes
    }

    // Compare current with previous to find changes
    currentValues.forEach { (key, currentValue) ->
        val previousValue = previousValues[key]
        val flagDescriptor = flags.flags[key]

        if (flagDescriptor != null && currentValue is JsonObject) {
            val wasEnabled = (previousValue as? JsonObject)?.get("enabled")?.jsonPrimitive?.booleanOrNull
            val isEnabled = currentValue["enabled"]?.jsonPrimitive?.booleanOrNull ?: false

            // Check if enabled state changed or if this is a new flag
            if (previousValue == null || wasEnabled != isEnabled || currentValue != previousValue) {
                changes.add(
                    ChangedFlag(
                        key = key,
                        flagName = flagDescriptor.name,
                        wasEnabled = wasEnabled,
                        isEnabled = isEnabled,
                        attributes = currentValue.filterKeys { it != "enabled" } as? JsonObject
                    )
                )
            }
        }
    }

    // Check for removed flags
    previousValues.forEach { (key, previousValue) ->
        if (!currentValues.containsKey(key)) {
            val flagDescriptor = flags.flags[key]
            if (flagDescriptor != null && previousValue is JsonObject) {
                val wasEnabled = previousValue["enabled"]?.jsonPrimitive?.booleanOrNull ?: false
                changes.add(
                    ChangedFlag(
                        key = key,
                        flagName = flagDescriptor.name,
                        wasEnabled = wasEnabled,
                        isEnabled = false,
                        attributes = null
                    )
                )
            }
        }
    }

    return changes
}

/**
 * Displays the history of feature flag changes in a paginated, expandable table.
 *
 * This component provides a read-only view of feature flag configuration changes over time.
 * Each history entry shows:
 * - When the change was made (timestamp)
 * - Who made the change (user ID)
 * - The version of the configuration
 * - How many flags were changed
 *
 * Users can expand each row to see detailed information about which specific flags changed,
 * including their previous and current enabled states. Visual indicators (green/red pills)
 * make it easy to identify enabled vs disabled flags.
 *
 * The component supports pagination to navigate through older versions of the configuration,
 * and intelligently calculates the delta between consecutive versions to show only what changed.
 *
 * @param aggregatorService The aggregator service instance used to fetch feature flag data and history
 */
@Composable
fun featureFlagHistory(aggregatorService: AggregatorService) {
    var historyEntries by remember { mutableStateOf<List<FeatureFlagHistoryEntry>?>(null) }
    var featureFlags by remember { mutableStateOf<AppConfigFeatureFlagsDocument?>(null) }

    // Load feature flags metadata
    LaunchedEffect(true) {
        try {
            featureFlags = aggregatorService.clientApi.getFeatureFlags()
        } catch (e: Throwable) {
            aggregatorService.notifications.error("Failed to load feature flags metadata: ${e.message}")
        }
    }

    box({
        paddedContent = false
    }) {
        // Pagination control must be rendered first to trigger initial load
        LazyPaginationControl(
            key = Unit,
            updater = { items ->
                historyEntries = items
            },
            fetcher = { token ->
                val result = aggregatorService.clientApi.getFeatureFlagHistory(
                    limit = 20,
                    nextToken = token
                )
                PageContents(result.entries, result.nextToken)
            },
            onError = { error ->
                aggregatorService.notifications.error("Failed to load feature flag history: ${error.message}")
            }
        )

        // Display content based on loaded state
        if (historyEntries == null) {
            boxSpinner("Loading history...")
        } else if (historyEntries!!.isEmpty()) {
            Div({
                style {
                    textAlign("center")
                    padding(40.px)
                }
            }) {
                Text("No feature flag history available.")
            }
        } else {
            featureFlags?.let { flags ->
                val entries = historyEntries!!
                table<FeatureFlagHistoryEntry> {
                    items(entries)

                    expandable { entry ->
                        val index = entries.indexOf(entry)
                        val previousEntry = if (index < entries.size - 1) {
                            entries[index + 1]
                        } else {
                            null
                        }
                        val changedFlags = calculateChangedFlags(entry, previousEntry, flags)

                        Div({
                            style {
                                padding(10.px, 20.px)
                            }
                        }) {

                            // Show message for oldest entry
                            if (previousEntry == null && changedFlags.isNotEmpty()) {
                                Div({
                                    style {
                                        marginBottom(10.px)
                                        fontSize(12.px)
                                        color(rgba(0, 0, 0, 0.6))
                                    }
                                }) {
                                    Text("(Initial configuration - showing all flags)")
                                }
                            }

                            // Display changed flags
                            if (changedFlags.isEmpty()) {
                                Div({
                                    style {
                                        color(rgba(0, 0, 0, 0.6))
                                        fontStyle("italic")
                                    }
                                }) {
                                    Text("No flags changed")
                                }
                            } else {
                                changedFlags.forEach { flag ->
                                    Div({
                                        style {
                                            padding(6.px, 0.px)
                                            display(DisplayStyle.Flex)
                                            gap(8.px)
                                            property("align-items", "center")
                                        }
                                    }) {
                                        Div({
                                            style {
                                                fontWeight(600)
                                                minWidth(200.px)
                                            }
                                        }) {
                                            Text(flag.flagName)
                                        }
                                        Div({
                                            style {
                                                fontSize(12.px)
                                                padding(2.px, 8.px)
                                                borderRadius(3.px)
                                                if (flag.isEnabled) {
                                                    backgroundColor(SXMUI.messageSuccessBackground.value())
                                                    color(SXMUI.messageSuccessForeground.value())
                                                    border(1.px, LineStyle.Solid, SXMUI.messageSuccessBorder.value())
                                                } else {
                                                    backgroundColor(SXMUI.messageAlertBackground.value())
                                                    color(SXMUI.messageAlertForeground.value())
                                                    border(1.px, LineStyle.Solid, SXMUI.messageAlertBorder.value())
                                                }
                                            }
                                        }) {
                                            Text(if (flag.isEnabled) "enabled" else "disabled")
                                        }

                                        // Show previous state if available
                                        if (flag.wasEnabled != null && flag.wasEnabled != flag.isEnabled) {
                                            Div({
                                                style {
                                                    fontSize(11.px)
                                                    color(rgba(0, 0, 0, 0.5))
                                                    marginLeft(4.px)
                                                }
                                            }) {
                                                Text("(was ${if (flag.wasEnabled) "enabled" else "disabled"})")
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    column {
                        content { entry ->
                            Div({
                                style {
                                    fontSize(13.px)
                                    fontStyle("italic")
                                }
                            }) {
                                // Render in the globally-selected timezone (header picker).
                                Text(entry.timestamp.toLocalDateTimeIn(currentTimeZoneId()).toLocalDateTimeString())
                            }
                        }
                    }

                    column("User") {
                        content { entry ->
                            Text(entry.userId)
                        }
                    }

                    column {
                        content { entry ->
                            entry.description?.let {
                                Text(it)
                            }
                        }
                    }
                }
            }
        }
    }
}
